// src/pages/AmbassadorProgram.jsx
import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, Users, TrendingUp, Gift, CheckCircle, Star, Target, 
  MessageSquare, Share2, Heart, Zap, Crown, Mail, AlertCircle 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { API_CONFIG, fetchAPI, submitAmbassadorApplication } from '../api/config';

function AmbassadorProgram() {
  const navigate = useNavigate();
  const { user, addToast } = useStore();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    applicationType: '',
    socialPlatform: '',
    socialHandle: '',
    followers: '',
    financialSituation: '',
    motivation: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applicationError, setApplicationError] = useState('');
  const [localSaveSuccess, setLocalSaveSuccess] = useState(false);

  const benefits = [
    {
      icon: Crown,
      title: 'Saints Pro Access',
      description: 'Full lifetime access to all courses, certifications, and premium features'
    },
    {
      icon: Gift,
      title: 'R0 Cost',
      description: 'Complete access worth R3990/year at absolutely no cost to you'
    },
    {
      icon: Users,
      title: 'Exclusive Community',
      description: 'Network with fellow ambassadors and industry leaders in private channels'
    },
    {
      icon: TrendingUp,
      title: 'Early Access & Influence',
      description: 'Get first look at new content and help shape platform development'
    },
    {
      icon: Award,
      title: 'Official Recognition',
      description: 'Featured on our website, social media, and receive ambassador badge'
    },
    {
      icon: Zap,
      title: 'Career Opportunities',
      description: 'Priority access to job postings and partner opportunities'
    }
  ];

  const programPaths = [
    {
      type: 'social',
      icon: Share2,
      title: 'Social Ambassador Track',
      description: 'For content creators and influencers who want to promote quality education',
      requirements: [
        'Share Saints Academy content 2x per week across your platforms',
        'Maintain active engagement in our community forums',
        'Provide monthly feedback on content and platform experience',
        'Help create authentic content about your learning journey'
      ]
    },
    {
      type: 'financial', 
      icon: Heart,
      title: 'Scholarship Track',
      description: 'For passionate learners facing financial barriers to education',
      requirements: [
        'Demonstrate genuine financial need',
        'Commit to completing 2+ courses per quarter',
        'Actively participate in community discussions',
        'Share your success stories to inspire others'
      ]
    }
  ];

  const handlePathSelection = (pathType) => {
    if (!user) {
      localStorage.setItem('lastVisitedPage', '/ambassador-program');
      navigate('/auth');
      return;
    }
    setFormData({ ...formData, applicationType: pathType });
    setApplicationError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      localStorage.setItem('lastVisitedPage', '/ambassador-program');
      navigate('/auth');
      return;
    }

    if (!formData.applicationType) {
      setApplicationError('Please select a program path');
      return;
    }

    // Validate required fields based on path
    if (formData.applicationType === 'social') {
      if (!formData.socialPlatform || !formData.socialHandle) {
        setApplicationError('Please complete all social media fields');
        return;
      }
    }

    if (formData.applicationType === 'financial' && !formData.financialSituation) {
      setApplicationError('Please describe your financial situation');
      return;
    }

    if (!formData.motivation) {
      setApplicationError('Please tell us why you want to join');
      return;
    }

    setLoading(true);
    setApplicationError('');
    setLocalSaveSuccess(false);
    
    try {
      // Prepare application data
      const applicationData = {
        user_id: user.id,
        name: formData.name || user.name,
        email: formData.email || user.email,
        application_type: formData.applicationType,
        social_platform: formData.socialPlatform,
        social_handle: formData.socialHandle,
        followers: formData.followers,
        financial_situation: formData.financialSituation,
        motivation: formData.motivation
      };

      // Submit via API
      const result = await submitAmbassadorApplication(applicationData);
      
      if (result.success) {
        setSubmitted(true);
        setLocalSaveSuccess(false);
        setFormData({
          name: '',
          email: '',
          applicationType: '',
          socialPlatform: '',
          socialHandle: '',
          followers: '',
          financialSituation: '',
          motivation: ''
        });
        
        // Show toast notification
        addToast({
          message: 'Ambassador application submitted successfully! We\'ll review it within 5 business days.',
          type: 'achievement'
        });
        
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
      } else {
        throw new Error(result.error || 'Failed to submit application');
      }
      
    } catch (error) {
      console.error('Application error:', error);
      
      // Fallback: Save locally
      const localSaved = API_CONFIG.storeLocally('ambassador_applications', {
        user_id: user.id,
        name: formData.name || user.name,
        email: formData.email || user.email,
        application_type: formData.applicationType,
        ...formData,
        submitted_at: new Date().toISOString()
      });
      
      if (localSaved) {
        setLocalSaveSuccess(true);
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          applicationType: '',
          socialPlatform: '',
          socialHandle: '',
          followers: '',
          financialSituation: '',
          motivation: ''
        });
        
        addToast({
          message: 'Application saved locally. It will sync when connection is restored.',
          type: 'points'
        });
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
      } else {
        setApplicationError('Failed to save application. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full text-center"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {localSaveSuccess ? 'Application Saved Locally!' : 'Application Submitted!'}
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              {localSaveSuccess 
                ? 'Your application has been saved on your device. We\'ll sync it when the server is available.'
                : 'Thank you for your interest in the Saints Ambassador Program. Our team will review your application and get back to you within 5 business days.'}
            </p>
            <p className="text-sm text-gray-500 mb-8">
              Successful applicants will receive full Saints Pro access with lifetime benefits.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md"
              >
                Return to Home
              </button>
              <button
                onClick={() => setSubmitted(false)}
                className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200"
              >
                Submit Another Application
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-600 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Star className="w-5 h-5 text-yellow-300" />
              <span className="font-medium">Saints Ambassador Program</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Earn Saints Pro Access</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Join our mission to make AI education accessible. Get <strong>full Pro access</strong> (worth R3990/year) by contributing to our community through social promotion or by demonstrating financial need.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Ambassador Benefits</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              All approved ambassadors receive the complete Saints Pro experience at no cost.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-200"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Program Paths */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Choose Your Path</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Select the track that best fits your situation and goals.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {programPaths.map((path, index) => {
                const Icon = path.icon;
                return (
                  <motion.div
                    key={path.type}
                    initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-200"
                  >
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{path.title}</h3>
                        <p className="text-gray-600 text-sm">{path.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <h4 className="font-semibold text-gray-900">Requirements:</h4>
                      {path.requirements.map((req, reqIndex) => (
                        <div key={reqIndex} className="flex items-start space-x-3">
                          <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-green-600 text-sm font-bold">✓</span>
                          </div>
                          <span className="text-gray-700 text-sm">{req}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => handlePathSelection(path.type)}
                      className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 ${
                        formData.applicationType === path.type
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {formData.applicationType === path.type ? 'Selected' : 'Choose This Path'}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Application Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">Apply Now</h2>
              
              {applicationError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
                    <span className="text-red-700">{applicationError}</span>
                  </div>
                </div>
              )}
              
              {!formData.applicationType ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-gray-600 text-lg">Please select a program path above to continue your application.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name {!user?.name && '*'}
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required={!user?.name}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={user?.name || 'John Doe'}
                      />
                      {user?.name && (
                        <p className="text-xs text-gray-500 mt-1">Using your account name: {user.name}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address {!user?.email && '*'}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required={!user?.email}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={user?.email || 'you@example.com'}
                      />
                      {user?.email && (
                        <p className="text-xs text-gray-500 mt-1">Using your account email: {user.email}</p>
                      )}
                    </div>
                  </div>

                  {formData.applicationType === 'social' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="socialPlatform" className="block text-sm font-medium text-gray-700 mb-2">
                            Primary Social Platform *
                          </label>
                          <select
                            id="socialPlatform"
                            name="socialPlatform"
                            required
                            value={formData.socialPlatform}
                            onChange={(e) => setFormData({ ...formData, socialPlatform: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select platform</option>
                            <option value="instagram">Instagram</option>
                            <option value="twitter">Twitter/X</option>
                            <option value="linkedin">LinkedIn</option>
                            <option value="youtube">YouTube</option>
                            <option value="tiktok">TikTok</option>
                            <option value="facebook">Facebook</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="socialHandle" className="block text-sm font-medium text-gray-700 mb-2">
                            Social Media Handle *
                          </label>
                          <input
                            type="text"
                            id="socialHandle"
                            name="socialHandle"
                            required
                            value={formData.socialHandle}
                            onChange={(e) => setFormData({ ...formData, socialHandle: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="@yourusername"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="followers" className="block text-sm font-medium text-gray-700 mb-2">
                          Follower Count *
                        </label>
                        <select
                          id="followers"
                          name="followers"
                          required
                          value={formData.followers}
                          onChange={(e) => setFormData({ ...formData, followers: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select range</option>
                          <option value="0-1k">0 - 1,000</option>
                          <option value="1k-5k">1,000 - 5,000</option>
                          <option value="5k-10k">5,000 - 10,000</option>
                          <option value="10k-50k">10,000 - 50,000</option>
                          <option value="50k+">50,000+</option>
                        </select>
                      </div>
                    </>
                  )}

                  {formData.applicationType === 'financial' && (
                    <div>
                      <label htmlFor="financialSituation" className="block text-sm font-medium text-gray-700 mb-2">
                        Financial Situation *
                      </label>
                      <textarea
                        id="financialSituation"
                        name="financialSituation"
                        required
                        value={formData.financialSituation}
                        onChange={(e) => setFormData({ ...formData, financialSituation: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Please briefly describe your financial situation and why you need scholarship access..."
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="motivation" className="block text-sm font-medium text-gray-700 mb-2">
                      Why do you want to join the Saints Ambassador Program? *
                    </label>
                    <textarea
                      id="motivation"
                      name="motivation"
                      required
                      value={formData.motivation}
                      onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
                      rows={5}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Tell us about your passion for education, your goals, and how you plan to contribute to our community..."
                    />
                  </div>

                  <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="privacy"
                      name="privacy"
                      required
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="privacy" className="text-sm text-gray-700">
                      I agree to receive emails about my application status and program updates. I have read and agree to the program terms.
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Submitting Application...</span>
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        <span>Submit Application</span>
                      </>
                    )}
                  </button>

                  <p className="text-sm text-gray-500 text-center">
                    We review applications on a rolling basis and will respond within 5 business days.
                    <br />
                    You will receive a confirmation email after submission.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default AmbassadorProgram;